import { useCallback, useState } from 'react';
import { getVendors } from '../../services/vendorService';
import { triggerReconcile } from '../../services/reconciliationApi';

export interface RerunResult {
  /** Batches processed across supplier vendors. */
  processed: number;
  /** True if any vendor's run is still finishing server-side (large batch). */
  running: boolean;
}

/**
 * Re-run reconciliation over the **existing** latest batches — Control 1
 * (seat/term) + Control 2 (coverage), no Synnex re-pull. Iterates the
 * subscription-supplier vendors and calls `triggerReconcile` per vendor (which
 * resolves each vendor's current latest batch). Optional `scope` narrows to one
 * SKU / customer identifier.
 */
export function useRerunReconcile() {
  const [running, setRunning] = useState(false);

  const rerun = useCallback(
    async (scope?: { onlySku?: string; onlyCustomer?: string }): Promise<RerunResult> => {
      setRunning(true);
      try {
        const suppliers = (await getVendors()).filter((v) => v.isSubscriptionSupplier);
        let processed = 0;
        let anyRunning = false;
        for (const v of suppliers) {
          const res = await triggerReconcile({ vendorId: v.vendorId, ...scope });
          processed += res.processed ?? 0;
          if (res.running) anyRunning = true;
        }
        return { processed, running: anyRunning };
      } finally {
        setRunning(false);
      }
    },
    [],
  );

  return { rerun, running };
}
